/**
 * Journal voucher viewer — a branded, print-ready A5 *landscape* document for a
 * single posted journal entry. Mirrors the invoice print aesthetic (white paper,
 * brand band from the active palette's BASE primary so it reads on white in light
 * or dark mode) but is laid out as a classic double-entry voucher: a debit/credit
 * line table that balances, narration, amount in words, and signature slots.
 *
 * No app shell: a slim viewer chrome (back · print) over the paper. Ctrl/⌘+P (or
 * the Print button) renders it to PDF via the browser — `@page { size: A5
 * landscape }` so it always lands on one A5 landscape sheet for a typical entry.
 */

import type { JournalEntry } from '@erp/shared';
import { ArrowLeft, Printer } from 'lucide-react';
import { type CSSProperties, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { amountInWords } from '../../lib/amountInWords.js';
import { api } from '../../lib/api.js';
import { getBrand } from '../../lib/brandClient.js';
import { formatDate, formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { PALETTES, loadPalette, swatchAccent } from '../../lib/palette.js';
import { assetUrl } from '../../lib/publicApi.js';

export function JournalVoucherPrintPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation('gl');
  const { t: tc } = useTranslation();
  const brand = getBrand();
  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [tenant, setTenant] = useState<{ name: string; baseCurrencyCode: string } | null>(null);
  // The store logo, same source the invoice paper uses (/cms/settings →
  // public storefront media URL). Best-effort: no logo ⇒ the monogram chip.
  const [seller, setSeller] = useState<{ logoAttachmentId: string | null; slug: string } | null>(null);
  const fitRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Paper brand = the active palette's BASE primary (saturated; reads on white).
  const { paperBrand, paperAccent } = useMemo(() => {
    const p = PALETTES.find((x) => x.id === loadPalette()) ?? PALETTES[0]!;
    return { paperBrand: p.primary, paperAccent: swatchAccent(p) };
  }, []);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      const r = await api().get<JournalEntry>(`/journal-entries/${id}`);
      setEntry(r.data);
      const tn = await api().get<{ name: string; baseCurrencyCode: string }>('/tenants/current');
      setTenant({ name: tn.data.name, baseCurrencyCode: tn.data.baseCurrencyCode });
      await api().get<{ logoAttachmentId: string | null; slug: string }>('/cms/settings')
        .then((s) => setSeller(s.data))
        .catch(() => setSeller(null));
    })();
  }, [id]);

  // Auto-fit: scale the voucher down so it always lands on a single A5 landscape
  // sheet, however many lines the entry has. offsetHeight is the *untransformed*
  // layout height, so reading it back after applying the transform can't feed a
  // loop. 559px ≈ 148mm at the paper's 794px (210mm) width.
  useLayoutEffect(() => {
    const el = fitRef.current;
    if (!el || !entry) return;
    const A5_LANDSCAPE_PX = 559;
    const measure = (): void => {
      const natural = el.offsetHeight;
      setScale(natural > A5_LANDSCAPE_PX ? A5_LANDSCAPE_PX / natural : 1);
    };
    measure();
    // Re-measure once webfonts settle — their metrics shift the first layout.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      void document.fonts.ready.then(measure);
    }
  }, [entry]);

  const locale = i18n.language;
  const isAr = i18n.dir(locale) === 'rtl';

  if (!entry) {
    return <p className="p-6 text-sm text-fg-muted">{tc('actions.loading', { defaultValue: 'Loading…' })}</p>;
  }

  // Single-currency entries (the common case) print in that currency — their
  // transaction debits/credits balance. Mixed-currency entries fall back to the
  // base currency (where double-entry always balances).
  const currencies = new Set(entry.lines.map((l) => l.transactionCurrencyCode));
  const single = currencies.size === 1;
  const cur = (single ? entry.lines[0]?.transactionCurrencyCode : tenant?.baseCurrencyCode) ?? tenant?.baseCurrencyCode ?? 'USD';
  const debitOf = (l: JournalEntry['lines'][number]): string => (single ? l.debit : l.baseDebit);
  const creditOf = (l: JournalEntry['lines'][number]): string => (single ? l.credit : l.baseCredit);
  const totalDebit = entry.lines.reduce((s, l) => s + Number(debitOf(l)), 0);
  const totalCredit = entry.lines.reduce((s, l) => s + Number(creditOf(l)), 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.005;

  const m = (amount: string | number): string =>
    formatMoney(typeof amount === 'number' ? amount.toFixed(4) : amount, { locale, currency: cur });
  const z = (amount: string): boolean => Number(amount) === 0;

  const sellerName = tenant?.name ?? brand.name.full;
  const sellerSub = isAr ? brand.name.tagline_ar : brand.name.tagline_en;
  const sellerLogo = seller?.logoAttachmentId && seller.slug
    ? assetUrl(`/api/v1/storefront/${seller.slug}/media/${seller.logoAttachmentId}`)
    : null;
  const sourceLabel = entry.sourceType ?? t('journals.source.manual', { defaultValue: 'Manual' });

  // Reversal / reversed entries get a watermark stamp.
  const stamp = entry.reversesId
    ? { cls: 'reversal', label: t('voucher.stampReversal', { defaultValue: 'Reversal' }) }
    : entry.reversedById
      ? { cls: 'reversed', label: t('voucher.stampReversed', { defaultValue: 'Reversed' }) }
      : null;

  const rootStyle = { '--paper-brand': paperBrand, '--paper-accent': paperAccent } as CSSProperties;

  return (
    <div className="jv" dir={i18n.dir(locale)} style={rootStyle}>
      <style>{CSS}</style>
      <style media="print">{'@page{size:A5 landscape;margin:0}'}</style>

      {/* viewer chrome */}
      <header className="jv-bar">
        <Link to="/accounting/journals" className="jv-back">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          <span>{tc('actions.back', { defaultValue: 'Back' })}</span>
        </Link>
        <span className="jv-title">{t('voucher.title', { defaultValue: 'Journal Voucher' })} · <span className="font-mono">{entry.entryNumber}</span></span>
        <span className="jv-spacer" />
        <button type="button" className="jv-print" onClick={() => window.print()}>
          <Printer className="h-4 w-4" aria-hidden />
          <span>{t('voucher.print', { defaultValue: 'Print / PDF' })}</span>
        </button>
      </header>

      <div className="jv-stage">
        <div className="a5" style={scale < 1 ? { height: 559 } : undefined}>
          <div
            className="a5-fit"
            ref={fitRef}
            style={scale < 1 ? { transform: `scale(${scale})`, transformOrigin: 'top center' } : undefined}
          >
            {stamp && <div className={`stamp ${stamp.cls}`}>{stamp.label}</div>}

          {/* header band */}
          <div className="jv-top">
            <div className="brand">
              {sellerLogo ? (
                <img className="mklogo" src={sellerLogo} alt={sellerName} />
              ) : (
                <div className="mk" aria-hidden>{brand.name.short?.slice(0, 2) || 'IQ'}</div>
              )}
              <div>
                <div className="co">{sellerName}</div>
                {sellerSub && <div className="cosub">{sellerSub}</div>}
              </div>
            </div>
            <div className="doc">
              <div className="dt">{t('voucher.title', { defaultValue: 'Journal Voucher' })}</div>
              <div className="dn">{entry.entryNumber}</div>
            </div>
          </div>

          {/* meta strip */}
          <div className="jv-meta">
            <div className="mi"><div className="k">{t('voucher.date', { defaultValue: 'Date' })}</div><div className="v">{formatDate(entry.entryDate, { locale })}</div></div>
            <div className="mi"><div className="k">{t('voucher.source', { defaultValue: 'Source' })}</div><div className="v">{sourceLabel}</div></div>
            <div className="mi"><div className="k">{t('voucher.currency', { defaultValue: 'Currency' })}</div><div className="v">{cur}</div></div>
            <div className="mi"><div className="k">{t('voucher.lines', { defaultValue: 'Lines' })}</div><div className="v">{entry.lines.length}</div></div>
            <span className={`chip ${balanced ? 'ok' : 'bad'}`}>
              {balanced ? t('voucher.balanced', { defaultValue: 'Balanced' }) : t('voucher.notBalanced', { defaultValue: 'Out of balance' })}
            </span>
          </div>

          {/* lines */}
          <div className="jv-body">
            <table className="lines">
              <thead>
                <tr>
                  <th className="idx">#</th>
                  <th className="l">{t('voucher.col.account', { defaultValue: 'Account' })}</th>
                  <th className="l">{t('voucher.col.description', { defaultValue: 'Description' })}</th>
                  <th>{t('voucher.col.debit', { defaultValue: 'Debit' })}</th>
                  <th>{t('voucher.col.credit', { defaultValue: 'Credit' })}</th>
                </tr>
              </thead>
              <tbody>
                {entry.lines.map((l) => (
                  <tr key={l.lineNo}>
                    <td className="idx">{l.lineNo}</td>
                    <td className="l">
                      <span className="acode">{l.accountCode}</span>
                      <span className="aname">{l.accountName}</span>
                    </td>
                    <td className="l desc">
                      {l.description ?? '—'}
                      {/* Mixed-currency voucher prints base amounts — annotate each
                          foreign line with its original amount + the rate applied. */}
                      {!single && l.transactionCurrencyCode !== l.baseCurrencyCode && (
                        <span className="muted" style={{ display: 'block', fontSize: '0.85em', direction: 'ltr' }}>
                          {formatMoney(Number(l.debit) > 0 ? l.debit : l.credit, { locale, currency: l.transactionCurrencyCode })}
                          {' @ '}
                          {/* Latin digits pinned: a printed voucher is an audit artefact whose
                              rate gets transcribed back into the books, so it must not follow
                              the staff user's numerals toggle (the amount above it is Latin). */}
                          {formatNumber(l.exchangeRate, { locale, numberingSystem: 'latn' }, { maximumSignificantDigits: 8 })}
                        </span>
                      )}
                    </td>
                    <td className={z(debitOf(l)) ? 'muted' : 'amt'}>{z(debitOf(l)) ? '—' : m(debitOf(l))}</td>
                    <td className={z(creditOf(l)) ? 'muted' : 'amt'}>{z(creditOf(l)) ? '—' : m(creditOf(l))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="idx" />
                  <td className="l tot">{t('voucher.totals', { defaultValue: 'Totals' })}</td>
                  <td className="l" />
                  <td className="amt tot">{m(totalDebit)}</td>
                  <td className="amt tot">{m(totalCredit)}</td>
                </tr>
              </tfoot>
            </table>

            <div className="below">
              <div className="narr">
                {entry.description && (
                  <div className="blk">
                    <div className="k">{t('voucher.narration', { defaultValue: 'Narration' })}</div>
                    <div className="v">{entry.description}</div>
                  </div>
                )}
                <div className="blk words">
                  <div className="k">{t('voucher.amountInWords', { defaultValue: 'Amount in words' })}</div>
                  <div className="v">{amountInWords(totalDebit, cur, locale)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* footer: signatures + posted info */}
          <div className="jv-foot">
            <div className="sigs">
              <div className="sig"><div className="line">{t('voucher.preparedBy', { defaultValue: 'Prepared by' })}</div></div>
              <div className="sig"><div className="line">{t('voucher.checkedBy', { defaultValue: 'Checked by' })}</div></div>
              <div className="sig"><div className="line">{t('voucher.approvedBy', { defaultValue: 'Approved by' })}</div></div>
            </div>
            <div className="postline">
              <span>{sellerName}</span>
              <span>{t('voucher.postedAt', { defaultValue: 'Posted' })}: {formatDate(entry.postedAt, { locale })}</span>
            </div>
            <span className="paper-strip" aria-hidden />
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Scoped paper styles. The paper itself is always white with dark text (a real
   printable document); only the brand band/accents use --paper-brand/-accent. */
const CSS = `
.jv { min-height: 100vh; display: flex; flex-direction: column; background: var(--color-bg-subtle); color: var(--color-fg); }
.jv-bar { position: sticky; top: 0; z-index: 10; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  min-height: 56px; padding: 8px 20px; border-bottom: 1px solid var(--color-border); background: var(--color-bg); }
.jv-back { display: inline-flex; align-items: center; gap: 7px; height: 36px; padding: 0 12px; border-radius: 8px;
  color: var(--color-fg-muted); font-size: 13px; font-weight: 600; border: 1px solid var(--color-border); background: var(--color-bg-subtle); }
.jv-back:hover { color: var(--color-fg); }
[dir="rtl"] .jv-back svg { transform: scaleX(-1); }
.jv-title { font-weight: 800; font-size: 14px; }
.jv-spacer { flex: 1; }
.jv-print { display: inline-flex; align-items: center; gap: 7px; height: 36px; padding: 0 14px; border-radius: 8px;
  border: 0; background: var(--color-brand); color: var(--color-brand-contrast); font-size: 13px; font-weight: 700; cursor: pointer; }
.jv-stage { flex: 1; overflow: auto; padding: 32px 20px 64px; display: flex; flex-direction: column; align-items: center; }

/* A5 landscape: 210mm x 148mm ≈ 794px x 559px */
.a5 { width: 794px; max-width: 100%; min-height: 559px; background: #fff; color: #1a2230;
  box-shadow: 0 30px 80px -20px rgba(0,0,0,.45), 0 8px 24px -8px rgba(0,0,0,.3); border-radius: 3px;
  position: relative; overflow: hidden; }
/* Inner content column; scaled down (transform) to fit one sheet for long entries. */
.a5-fit { width: 100%; min-height: 559px; display: flex; flex-direction: column; }

.stamp { position: absolute; top: 150px; inset-inline-end: 54px; z-index: 5; font-size: 40px; font-weight: 900;
  letter-spacing: 4px; text-transform: uppercase; padding: 6px 20px; border: 5px solid currentColor; border-radius: 12px;
  transform: rotate(-12deg); opacity: .12; pointer-events: none; white-space: nowrap; }
[dir="rtl"] .stamp { transform: rotate(12deg); letter-spacing: 0; }
.stamp.reversal { color: #2C6C88; } .stamp.reversed { color: #AE3A33; }

.jv-top { padding: 20px 30px 18px; color: #fff; display: flex; align-items: flex-start; justify-content: space-between; gap: 20px;
  background: linear-gradient(135deg, var(--paper-brand), color-mix(in oklab, var(--paper-brand) 58%, #07071c));
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.jv-top .brand { display: flex; align-items: flex-start; gap: 12px; min-width: 0; }
.jv-top .mk { width: 44px; height: 44px; border-radius: 11px; background: rgba(255,255,255,.16); border: 1px solid rgba(255,255,255,.24);
  display: grid; place-items: center; flex: none; font-weight: 800; font-size: 15px; letter-spacing: .04em; }
/* The real store logo, on a white chip so it reads on the dark brand band. */
.jv-top .mklogo { width: 44px; height: 44px; border-radius: 11px; background: #fff; border: 1px solid rgba(255,255,255,.24);
  object-fit: contain; padding: 3px; flex: none; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.jv-top .co { font-size: 18px; font-weight: 800; letter-spacing: -.02em; line-height: 1.15; }
.jv-top .cosub { font-size: 11px; opacity: .82; margin-top: 2px; }
.jv-top .doc { text-align: end; flex: none; }
.jv-top .doc .dt { font-size: 20px; font-weight: 800; letter-spacing: .02em; text-transform: uppercase; }
[dir="rtl"] .jv-top .doc .dt { letter-spacing: 0; }
.jv-top .doc .dn { font-size: 12px; opacity: .9; margin-top: 3px; font-variant-numeric: tabular-nums; }

.jv-meta { display: flex; align-items: center; gap: 26px; padding: 12px 30px; border-bottom: 1px solid #ece8de; flex-wrap: wrap; }
.jv-meta .mi .k { font-size: 9px; color: #888FA5; text-transform: uppercase; letter-spacing: .05em; font-weight: 700; }
[dir="rtl"] .jv-meta .mi .k { letter-spacing: 0; }
.jv-meta .mi .v { font-size: 12.5px; font-weight: 700; margin-top: 1px; color: #1a2230; font-variant-numeric: tabular-nums; }
.jv-meta .chip { margin-inline-start: auto; font-size: 10px; font-weight: 800; padding: 4px 10px; border-radius: 999px; text-transform: uppercase; letter-spacing: .04em; }
[dir="rtl"] .jv-meta .chip { letter-spacing: 0; }
.jv-meta .chip.ok { background: #E4F4EC; color: #1F7A52; } .jv-meta .chip.bad { background: #FBE7E5; color: #AE3A33; }

.jv-body { padding: 16px 30px 0; display: flex; flex-direction: column; flex: 1; }
table.lines { width: 100%; border-collapse: collapse; }
.lines thead th { background: var(--paper-brand); color: #fff; font-size: 10px; font-weight: 700; letter-spacing: .04em;
  text-transform: uppercase; padding: 8px 11px; text-align: end; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
[dir="rtl"] .lines thead th { letter-spacing: 0; }
.lines thead th.l { text-align: start; } .lines thead th.idx { text-align: center; width: 30px; }
.lines thead th:first-child { border-start-start-radius: 8px; }
.lines thead th:last-child { border-start-end-radius: 8px; }
.lines tbody td { padding: 8px 11px; font-size: 12px; border-bottom: 1px solid #ece8de; text-align: end; font-variant-numeric: tabular-nums; }
.lines tbody td.l { text-align: start; }
.lines tbody tr:nth-child(even) td { background: #F4F6FB; }
.lines td.idx { color: #888FA5; font-weight: 700; text-align: center; }
.lines td .acode { font-family: ui-monospace, monospace; font-weight: 700; color: var(--paper-brand); margin-inline-end: 7px; }
.lines td .aname { color: #1a2230; font-weight: 600; }
.lines td.desc { color: #545B72; }
.lines td.amt { font-weight: 700; color: #1a2230; }
.lines td.muted { color: #C6CDDC; }
.lines tfoot td { padding: 9px 11px; font-size: 12.5px; border-top: 2px solid var(--paper-brand); text-align: end; font-variant-numeric: tabular-nums; }
.lines tfoot td.l { text-align: start; }
.lines tfoot .tot { font-weight: 800; color: #1a2230; }

.below { margin-top: 12px; }
.narr { display: flex; flex-direction: column; gap: 8px; }
.narr .blk { padding: 8px 13px; background: #F4F6FB; border-radius: 8px; border: 1px solid #ece8de; }
.narr .k { font-size: 9px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #888FA5; }
[dir="rtl"] .narr .k { letter-spacing: 0; }
.narr .v { font-size: 12px; margin-top: 2px; color: #1a2230; line-height: 1.45; }
.narr .words .v { font-weight: 700; }

.jv-foot { margin-top: auto; flex: none; padding: 14px 30px 0; }
.sigs { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 30px; }
.sigs .sig .line { border-top: 1.5px solid #C6CDDC; padding-top: 6px; margin-top: 26px; font-size: 10px; color: #545B72; font-weight: 600; text-align: center; }
.postline { display: flex; justify-content: space-between; gap: 12px; padding: 10px 0; margin-top: 8px; font-size: 9.5px; color: #888FA5; border-top: 1px solid #ece8de; }
.paper-strip { display: block; height: 6px; margin: 0 -30px; background: linear-gradient(90deg, var(--paper-brand), var(--paper-accent));
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }

@media print {
  .jv { background: #fff; }
  .jv-bar { display: none !important; }
  .jv-stage { padding: 0; overflow: visible; display: block; }
  .a5 { box-shadow: none; border-radius: 0; width: 100%; min-height: 100vh; }
}
`;
